import { useMemo } from 'react';
import { GridRowParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import {
  DataGridConfig,
  DataGridConfigBuilder,
} from '../config/DataGridConfig';
import {
  DEFAULT_PAGINATION_CONFIG,
  DEFAULT_OPERATION_MODES,
  DEFAULT_SORT_MODEL,
} from '../constants';

/**
 * Hook for managing DataGrid configuration
 * Transforms configuration into props ready for use in DataGrid
 * 
 * Uses granular dependencies instead of entire config object
 * to avoid unnecessary recalculations when only some fields change
 */
export function useDataGridConfig<T extends GridValidRowModel>(
  config: DataGridConfig<T>
) {
  const {
    dataGridInstanceName,
    defaultPageSize = DEFAULT_PAGINATION_CONFIG.defaultPageSize,
    pageSizeOptions = DEFAULT_PAGINATION_CONFIG.pageSizeOptions,
    initialSortModel = DEFAULT_SORT_MODEL,
    initialFilterModel,
    initialColumnVisibilityModel,
    sortingMode = DEFAULT_OPERATION_MODES.sortingMode,
    filterMode = DEFAULT_OPERATION_MODES.filterMode,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
    onRowDoubleClick,
    noRowsOverlay,
    slotProps,
    slots,
    columnGroupingModel,
    getRowClassName,
    checkboxSelection,
    disableRowSelectionOnClick,
    disableMultipleRowSelection,
    hideFooterSelectedRowCount,
    rowSelectionModel,
    onRowSelectionModelChange,
    autosizeOptions,
    autosizeOnMount,
    disablePagination,
  } = config;

  return useMemo(() => {
    return {
      dataGridInstanceName,
      defaultPageSize,
      pageSizeOptions,
      initialSortModel,
      sortingMode,
      filterMode,
      sortModel,
      onSortModelChange,
      filterModel,
      onFilterModelChange,
      onRowDoubleClick,
      noRowsOverlay,
      slotProps,
      slots,
      columnGroupingModel,
      getRowClassName,
      checkboxSelection,
      disableRowSelectionOnClick,
      disableMultipleRowSelection,
      hideFooterSelectedRowCount,
      rowSelectionModel,
      onRowSelectionModelChange,
      autosizeOptions,
      autosizeOnMount,
      disablePagination,
      initialState: {
        pagination: {
          paginationModel: { pageSize: defaultPageSize },
        },
        sorting: {
          sortModel: initialSortModel,
        },
        ...(initialFilterModel && {
          filter: {
            filterModel: initialFilterModel,
          },
        }),
        ...(initialColumnVisibilityModel && {
          columns: {
            columnVisibilityModel: initialColumnVisibilityModel,
          },
        }),
      },
    };
  }, [
    dataGridInstanceName,
    defaultPageSize,
    pageSizeOptions,
    initialSortModel,
    initialFilterModel,
    initialColumnVisibilityModel,
    sortingMode,
    filterMode,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
    onRowDoubleClick,
    noRowsOverlay,
    slotProps,
    slots,
    columnGroupingModel,
    getRowClassName,
    checkboxSelection,
    disableRowSelectionOnClick,
    disableMultipleRowSelection,
    hideFooterSelectedRowCount,
    rowSelectionModel,
    onRowSelectionModelChange,
    autosizeOptions,
    autosizeOnMount,
    disablePagination,
  ]);
}

